import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  CheckCircleIcon,
  ClipboardDocumentIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn } from '@/utils/cn';
import {
  submitPublicIntake,
  type InvestigationType,
  type PublicIntakeSubmission,
  type Urgency,
  type SubjectType,
  type ReferralSource,
} from '@/services/publicIntake.service';
import { DateInput } from '@/components/ui/DateInput';
import { toDateValue } from '@/utils/datetime';

interface FormState {
  investigationType: InvestigationType | '';
  requesterName: string;
  requesterEmail: string;
  requesterPhone: string;
  requesterOrganization: string;
  requesterRole: string;
  subjectType: SubjectType;
  subjectName: string;
  subjectAddress: string;
  subjectDob: string;
  subjectIdentifiers: string;
  investigationDetails: string;
  desiredOutcome: string;
  urgency: Urgency;
  howHeardAboutUs: ReferralSource | '';
  website: string; // honeypot
}

type FormErrors = Partial<Record<keyof FormState | 'general', string>>;

const INITIAL_STATE: FormState = {
  investigationType: '',
  requesterName: '',
  requesterEmail: '',
  requesterPhone: '',
  requesterOrganization: '',
  requesterRole: '',
  subjectType: 'PERSON',
  subjectName: '',
  subjectAddress: '',
  subjectDob: '',
  subjectIdentifiers: '',
  investigationDetails: '',
  desiredOutcome: '',
  urgency: 'STANDARD',
  howHeardAboutUs: '',
  website: '',
};

const INVESTIGATION_TYPE_OPTIONS: Array<{ value: InvestigationType; label: string; description: string }> = [
  {
    value: 'INSURANCE_CLAIMS',
    label: 'Insurance Claims',
    description: 'Claim investigations, surveillance, or fraud verification.',
  },
  {
    value: 'CORPORATE_FRAUD',
    label: 'Corporate / Fraud',
    description: 'Internal investigations, misconduct, or fraud allegations.',
  },
  {
    value: 'BACKGROUND_CHECK',
    label: 'Background Check',
    description: 'Pre-employment, tenant, or due-diligence screening.',
  },
  {
    value: 'OTHER',
    label: 'Other',
    description: 'Anything not listed — we\u2019ll follow up to learn more.',
  },
];

const URGENCY_OPTIONS: Array<{ value: Urgency; label: string; description: string }> = [
  { value: 'STANDARD', label: 'Standard', description: 'Typical intake — begin within a few business days.' },
  { value: 'PRIORITY', label: 'Priority', description: 'Time-sensitive — begin within 48 hours.' },
  { value: 'URGENT', label: 'Urgent', description: 'Active risk — begin as soon as possible.' },
];

const REFERRAL_OPTIONS: Array<{ value: ReferralSource; label: string }> = [
  { value: 'REFERRAL', label: 'Referral' },
  { value: 'WEB_SEARCH', label: 'Web search' },
  { value: 'EXISTING_CLIENT', label: 'Existing client' },
  { value: 'OTHER', label: 'Other' },
];

const MAX_DETAILS = 10000;
const MAX_OUTCOME = 5000;

export default function PublicInvestigationIntake() {
  const [form, setForm] = useState<FormState>(INITIAL_STATE);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{
    referenceNumber: string;
    submittedAt: string;
  } | null>(null);
  const [copied, setCopied] = useState(false);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: undefined }));
    }
  };

  const validate = (): boolean => {
    const next: FormErrors = {};

    if (!form.investigationType) {
      next.investigationType = 'Please select an investigation type.';
    }
    if (!form.requesterName.trim()) {
      next.requesterName = 'Your name is required.';
    }
    if (!/^\S+@\S+\.\S+$/.test(form.requesterEmail)) {
      next.requesterEmail = 'A valid email is required.';
    }
    if (!form.requesterPhone.trim()) {
      next.requesterPhone = 'A contact phone number is required.';
    } else if (form.requesterPhone.replace(/\D/g, '').length < 7) {
      next.requesterPhone = 'Please enter a valid phone number.';
    }
    if (!form.requesterOrganization.trim()) {
      next.requesterOrganization = 'Company or organization is required.';
    }
    if (!form.subjectName.trim()) {
      next.subjectName =
        form.subjectType === 'PERSON'
          ? 'Subject full name is required.'
          : 'Business or entity name is required.';
    }
    if (form.investigationDetails.trim().length < 20) {
      next.investigationDetails =
        'Please provide at least 20 characters describing what needs to be investigated.';
    } else if (form.investigationDetails.length > MAX_DETAILS) {
      next.investigationDetails = `Please keep investigation details under ${MAX_DETAILS} characters.`;
    }
    if (form.desiredOutcome.length > MAX_OUTCOME) {
      next.desiredOutcome = `Please keep desired outcome under ${MAX_OUTCOME} characters.`;
    }
    if (form.subjectDob && isNaN(Date.parse(form.subjectDob))) {
      next.subjectDob = 'Please enter a valid date of birth.';
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) {
      setTimeout(() => {
        const firstError = document.querySelector('[data-error="true"]');
        firstError?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
      return;
    }

    setSubmitting(true);
    setErrors({});

    try {
      const payload: PublicIntakeSubmission = {
        investigationType: form.investigationType as InvestigationType,
        requesterName: form.requesterName.trim(),
        requesterEmail: form.requesterEmail.trim(),
        requesterPhone: form.requesterPhone.trim(),
        requesterOrganization: form.requesterOrganization.trim(),
        requesterRole: form.requesterRole.trim() || undefined,
        subjectType: form.subjectType,
        subjectName: form.subjectName.trim(),
        subjectAddress: form.subjectAddress.trim() || undefined,
        subjectDob: form.subjectDob || undefined,
        subjectIdentifiers: form.subjectIdentifiers.trim() || undefined,
        investigationDetails: form.investigationDetails.trim(),
        desiredOutcome: form.desiredOutcome.trim() || undefined,
        urgency: form.urgency,
        howHeardAboutUs: form.howHeardAboutUs || undefined,
        website: form.website,
      };

      const response = await submitPublicIntake(payload);
      setResult({
        referenceNumber: response.referenceNumber,
        submittedAt: response.submittedAt,
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'We could not submit your request. Please try again in a few minutes.';
      setErrors({ general: message });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async () => {
    if (!result?.referenceNumber) {return;}
    try {
      await navigator.clipboard.writeText(result.referenceNumber);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore — clipboard API may be blocked in embedded contexts
    }
  };

  if (result) {
    return (
      <div className="min-h-screen bg-secondary-50 dark:bg-secondary-900 py-12 px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-xl mx-auto"
        >
          <Card>
            <CardContent className="p-8 sm:p-10 text-center">
              <CheckCircleIcon className="w-16 h-16 text-success-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-secondary-900 dark:text-white mb-2">
                Request Received
              </h1>
              <p className="text-secondary-600 dark:text-secondary-400 mb-6">
                Thank you. A VisionFirm investigator will review your submission
                and reach out using the contact information you provided.
              </p>

              <div className="bg-secondary-50 dark:bg-secondary-800 rounded-lg p-4 mb-6 text-left">
                <p className="text-xs uppercase tracking-wide text-secondary-500 dark:text-secondary-400 mb-1">
                  Your reference number
                </p>
                <div className="flex items-center justify-between gap-3">
                  <code className="text-lg font-mono font-semibold text-secondary-900 dark:text-white break-all">
                    {result.referenceNumber}
                  </code>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleCopy}
                    className="shrink-0"
                  >
                    <ClipboardDocumentIcon className="w-4 h-4 mr-1" />
                    {copied ? 'Copied' : 'Copy'}
                  </Button>
                </div>
                <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-3">
                  Save this number. Use it in any follow-up email or phone call
                  about your request.
                </p>
              </div>

              <p className="text-sm text-secondary-500 dark:text-secondary-400">
                A VisionFirm investigator will contact you using the email or
                phone number you provided. Please save the reference number
                above for any follow-up correspondence.
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  const subjectIsPerson = form.subjectType === 'PERSON';

  return (
    <div className="min-h-screen bg-secondary-50 dark:bg-secondary-900 py-8 px-4 sm:px-6">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary-600/10 mb-4">
            <MagnifyingGlassIcon className="w-6 h-6 text-primary-600" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-secondary-900 dark:text-white">
            Submit an Investigation Request
          </h1>
          <p className="text-secondary-600 dark:text-secondary-400 mt-2 max-w-2xl mx-auto">
            Tell us what you need investigated and who to contact. A VisionFirm
            investigator will follow up. No account required.
          </p>
        </motion.div>

        {errors.general && (
          <div
            data-error="true"
            className="p-4 bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg flex items-start gap-2"
          >
            <ExclamationTriangleIcon className="w-5 h-5 text-danger-600 dark:text-danger-400 shrink-0 mt-0.5" />
            <p className="text-sm text-danger-800 dark:text-danger-200">
              {errors.general}
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6" noValidate>
          {/* Investigation Type */}
          <Card>
            <CardHeader>
              <CardTitle>What do you need investigated?</CardTitle>
              <CardDescription>
                Choose the option that best matches your situation.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div
                className="grid grid-cols-1 sm:grid-cols-2 gap-3"
                data-error={errors.investigationType ? 'true' : undefined}
              >
                {INVESTIGATION_TYPE_OPTIONS.map((opt) => {
                  const selected = form.investigationType === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => update('investigationType', opt.value)}
                      className={cn(
                        'text-left p-4 border-2 rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-primary-500',
                        selected
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/10'
                          : 'border-secondary-200 dark:border-secondary-700 bg-white dark:bg-secondary-800 hover:border-secondary-300 dark:hover:border-secondary-600',
                      )}
                    >
                      <div className="font-medium text-secondary-900 dark:text-white">
                        {opt.label}
                      </div>
                      <div className="text-sm text-secondary-600 dark:text-secondary-400 mt-1">
                        {opt.description}
                      </div>
                    </button>
                  );
                })}
              </div>
              {errors.investigationType && (
                <p className="text-sm text-danger-600 dark:text-danger-400">
                  {errors.investigationType}
                </p>
              )}

              <div className="space-y-2">
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300">
                  Urgency *
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {URGENCY_OPTIONS.map((opt) => {
                    const selected = form.urgency === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => update('urgency', opt.value)}
                        className={cn(
                          'text-left p-3 border-2 rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-primary-500',
                          selected
                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/10'
                            : 'border-secondary-200 dark:border-secondary-700 bg-white dark:bg-secondary-800',
                        )}
                      >
                        <div className="font-medium text-secondary-900 dark:text-white">
                          {opt.label}
                        </div>
                        <div className="text-xs text-secondary-600 dark:text-secondary-400 mt-1">
                          {opt.description}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Requester */}
          <Card>
            <CardHeader>
              <CardTitle>About you</CardTitle>
              <CardDescription>
                So we know who to contact about this request.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field
                  label="Full name *"
                  error={errors.requesterName}
                >
                  <Input
                    type="text"
                    value={form.requesterName}
                    onChange={(e) => update('requesterName', e.target.value)}
                    autoComplete="name"
                    maxLength={100}
                  />
                </Field>
                <Field
                  label="Company / Organization *"
                  error={errors.requesterOrganization}
                >
                  <Input
                    type="text"
                    value={form.requesterOrganization}
                    onChange={(e) =>
                      update('requesterOrganization', e.target.value)
                    }
                    autoComplete="organization"
                    maxLength={200}
                  />
                </Field>
                <Field
                  label="Email *"
                  error={errors.requesterEmail}
                  hint="An investigator will use this address to follow up."
                >
                  <Input
                    type="email"
                    value={form.requesterEmail}
                    onChange={(e) => update('requesterEmail', e.target.value)}
                    autoComplete="email"
                  />
                </Field>
                <Field
                  label="Phone *"
                  error={errors.requesterPhone}
                >
                  <Input
                    type="tel"
                    value={form.requesterPhone}
                    onChange={(e) => update('requesterPhone', e.target.value)}
                    autoComplete="tel"
                  />
                </Field>
                <Field
                  label="Role / Title"
                  hint="Optional — helps us understand context."
                  className="sm:col-span-2"
                >
                  <Input
                    type="text"
                    value={form.requesterRole}
                    onChange={(e) => update('requesterRole', e.target.value)}
                    maxLength={150}
                  />
                </Field>
              </div>
            </CardContent>
          </Card>

          {/* Subject */}
          <Card>
            <CardHeader>
              <CardTitle>About the subject</CardTitle>
              <CardDescription>
                Who or what is being investigated? Share what you know; fields
                marked optional can be left blank.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300">
                  Subject type *
                </label>
                <div className="inline-flex rounded-lg border border-secondary-200 dark:border-secondary-700 bg-white dark:bg-secondary-800 p-1">
                  {(['PERSON', 'ENTITY'] as const).map((value) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => update('subjectType', value)}
                      className={cn(
                        'px-4 py-1.5 text-sm font-medium rounded-md transition-colors',
                        form.subjectType === value
                          ? 'bg-primary-600 text-white'
                          : 'text-secondary-600 dark:text-secondary-300 hover:bg-secondary-100 dark:hover:bg-secondary-700',
                      )}
                    >
                      {value === 'PERSON' ? 'Person' : 'Entity / Business'}
                    </button>
                  ))}
                </div>
              </div>

              <Field
                label={
                  subjectIsPerson
                    ? 'Subject full name *'
                    : 'Business / Entity name *'
                }
                error={errors.subjectName}
              >
                <Input
                  type="text"
                  value={form.subjectName}
                  onChange={(e) => update('subjectName', e.target.value)}
                  maxLength={200}
                />
              </Field>

              <Field
                label={
                  subjectIsPerson
                    ? 'Last known address'
                    : 'Business address'
                }
                hint="Optional."
              >
                <textarea
                  value={form.subjectAddress}
                  onChange={(e) => update('subjectAddress', e.target.value)}
                  rows={2}
                  maxLength={500}
                  className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </Field>

              {subjectIsPerson && (
                <Field
                  label="Date of birth"
                  hint="Optional. Helps distinguish individuals with common names."
                  error={errors.subjectDob}
                >
                  <DateInput
                    value={form.subjectDob}
                    onChange={(value) => update('subjectDob', value)}
                    max={toDateValue(new Date())}
                  />
                </Field>
              )}

              <Field
                label="Identifiers"
                hint="Optional — SSN last 4, EIN, policy number, claim number, etc."
              >
                <Input
                  type="text"
                  value={form.subjectIdentifiers}
                  onChange={(e) =>
                    update('subjectIdentifiers', e.target.value)
                  }
                  maxLength={500}
                />
              </Field>
            </CardContent>
          </Card>

          {/* Details */}
          <Card>
            <CardHeader>
              <CardTitle>Investigation details</CardTitle>
              <CardDescription>
                What happened, what do you need investigated, and why?
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Field
                label="Details *"
                error={errors.investigationDetails}
                hint={`${form.investigationDetails.length}/${MAX_DETAILS} characters`}
              >
                <textarea
                  value={form.investigationDetails}
                  onChange={(e) =>
                    update('investigationDetails', e.target.value)
                  }
                  rows={6}
                  maxLength={MAX_DETAILS}
                  className={cn(
                    'w-full px-3 py-2 border rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
                    errors.investigationDetails
                      ? 'border-danger-300 dark:border-danger-600'
                      : 'border-secondary-300 dark:border-secondary-600',
                  )}
                />
              </Field>

              <Field
                label="Desired outcome / deliverables"
                hint="Optional — report, surveillance footage, records, etc."
                error={errors.desiredOutcome}
              >
                <textarea
                  value={form.desiredOutcome}
                  onChange={(e) => update('desiredOutcome', e.target.value)}
                  rows={3}
                  maxLength={MAX_OUTCOME}
                  className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </Field>

              <Field
                label="How did you hear about us?"
                hint="Optional."
              >
                <select
                  value={form.howHeardAboutUs}
                  onChange={(e) =>
                    update(
                      'howHeardAboutUs',
                      (e.target.value as ReferralSource) || '',
                    )
                  }
                  className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Select one...</option>
                  {REFERRAL_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </Field>
            </CardContent>
          </Card>

          {/* Honeypot — visually hidden, hidden from assistive tech. */}
          <div aria-hidden="true" className="absolute left-[-9999px] top-auto w-px h-px overflow-hidden">
            <label>
              Website
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={(e) => update('website', e.target.value)}
              />
            </label>
          </div>

          <div className="flex flex-col sm:flex-row sm:justify-end gap-3">
            <Button
              type="submit"
              disabled={submitting}
              size="lg"
              className="w-full sm:w-auto"
            >
              {submitting ? 'Submitting...' : 'Submit Request'}
            </Button>
          </div>

          <p className="text-xs text-secondary-500 dark:text-secondary-400 text-center flex items-center justify-center gap-1">
            <InformationCircleIcon className="w-3.5 h-3.5" />
            Your information is transmitted securely and used only to process
            this request.
          </p>
        </form>
      </div>
    </div>
  );
}

interface FieldProps {
  label: string;
  error?: string;
  hint?: string;
  className?: string;
  children: React.ReactNode;
}

function Field({ label, error, hint, className, children }: FieldProps) {
  return (
    <div className={cn('space-y-1.5', className)} data-error={error ? 'true' : undefined}>
      <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300">
        {label}
      </label>
      {children}
      {error && (
        <p className="text-sm text-danger-600 dark:text-danger-400">{error}</p>
      )}
      {!error && hint && (
        <p className="text-xs text-secondary-500 dark:text-secondary-400">
          {hint}
        </p>
      )}
    </div>
  );
}
